import {
  DownloadAdvancedOptions,
  DownloadAdvancedOptionsFile,
  EnquiresActionTypes
} from './types';
import { eventChannel, EventChannel } from 'redux-saga';
import {
  all,
  takeLatest,
  call,
  put,
  select,
  take,
  delay
} from 'redux-saga/effects';
import Services from '../../services';
import { initAppFailure, initAppSuccess } from 'redux/app/actions';
import {
  connectWebsocketForEnquiry,
  downloadEnquiryCombinedFileSuccess,
  downloadEnquiryCombinedFileFailure,
  updateEnquiryReferral,
  fetchEnquiryFailure,
  fetchEnquiryHistoryFailure,
  fetchEnquiryHistorySuccess,
  fetchEnquirySuccess,
  getAttachmentPreviewUrlSuccess,
  getAttachmentPreviewUrlFailure,
  getAttachmentDownloadUrlSuccess,
  getAttachmentDownloadUrlFailure,
  updateEnquiryByIdFailure,
  updateEnquiryByIdSuccess,
  getEnquiryConfirmationPDFDownloadUrlSuccess,
  getEnquiryConfirmationPDFDownloadUrlFailure,
  getDownloadAdvancedOptionsSuccess,
  getDownloadAdvancedOptionsFailure
} from './actions';
import Utils from 'utilities/helper.util';
import { Params } from 'redux/members/types';
import { fetchAuthFailure } from 'redux/auth/action';
import {
  getResponsesReceivedCount,
  getEnquiryStatusCodeMsg
} from 'appConstants/enquiry';
import { WebSocketConnectionOption, AUTH_TYPE } from 'types';
import { AppState } from 'redux/app/types';
import { updateLodgeEnquiryReferral } from 'redux/lodgeEnquiry/actions';
import { LodgeEnquiryState } from 'redux/lodgeEnquiry/types';
import { sortBy } from 'lodash';

export function* doFetchEnquiryById(request: any) {
  try {
    const response = yield call(
      Services.enquiry.fetchEnquiryByIdService,
      request.payload
    );
    if (!response.error) {
      yield put(fetchEnquirySuccess(response));
      yield put(initAppSuccess());
    } else {
      const errMsg = response.error.message;
      yield put(fetchEnquiryFailure(errMsg));
      yield put(fetchAuthFailure(errMsg));
      yield put(initAppFailure());
    }
  } catch (error) {
    const errorMsg = error.message ? error.message : error;
    yield put(fetchEnquiryFailure(errorMsg));
    yield put(fetchAuthFailure(errorMsg));
    yield put(initAppFailure());
  }
}

export function* doUpdateEnquiryById(request: any) {
  try {
    const response = yield call(
      Services.enquiry.fetchUpdateEnquiryByIdService,
      request.payload
    );

    if (!response.error) {
      yield put(updateEnquiryByIdSuccess(response));
    } else {
      const errMsg = response.error.message;
      yield put(updateEnquiryByIdFailure(errMsg));
      yield put(initAppFailure());
    }
  } catch (error) {
    const errorMsg = error.message ? error.message : error;
    yield put(updateEnquiryByIdFailure(errorMsg));
    yield put(initAppFailure());
  }
}

export function* doFetchEnquiryHistory(request: any) {
  const queryParams: Params = request.payload;
  try {
    const response = yield call(
      Services.enquiry.fetchAllEnquiriesService,
      queryParams
    );
    if (!response.errors) {
      const responseBody = response.Enquiries.map((item: any) => {
        const { receivedCount, totalCount } = getResponsesReceivedCount(
          item.Referrals
        );
        return {
          id: item.id,
          jobStartAt: Utils.toLocalDateFormat(item.createdAt),
          jobId: {
            job: item.externalId,
            start: Utils.toLocalDateFormat(item.createdAt)
          },
          userRef: item.userReference,
          status: item.status ? getEnquiryStatusCodeMsg(item.status) : 'TO DO',
          address: item.Address ? Utils.getFullAddress(item.Address) : '',
          responsesReceived: `${receivedCount}/${totalCount}`,
          enquirer: {
            fullname:
              item.User?.firstName && item.User?.lastName
                ? `${item.User.firstName} ${item.User.lastName}`
                : '',
            email: item.User?.email || ''
          },
          isSandboxTest: item.isSandboxTest
        };
      });

      const enquiries = { items: responseBody, count: response.Info?.count };
      yield put(fetchEnquiryHistorySuccess(enquiries));
      yield put(initAppSuccess());
    } else {
      yield put(fetchEnquiryHistoryFailure(response.errors[0].message));
    }
  } catch (error) {
    const errorMsg = error.message ? error.message : error;
    yield put(fetchEnquiryHistoryFailure(errorMsg));
    yield put(initAppFailure());
  }
}

export function* doConnectWebsocketForEnquiry(request: any) {
  const entityId = request.payload;
  try {
    const getAppState = (state: any) => state.app;
    const getLodgeEnquiryState = (state: any) => state.lodgeEnquiryCache;
    const appState: AppState = yield select(getAppState);
    const lodgeEnquiryState: LodgeEnquiryState = yield select(
      getLodgeEnquiryState
    );
    const isNewEnquiry = !Utils.isObjectEmpty(lodgeEnquiryState.enquiry);
    const auth: { user?: { username?: string; token?: string } } = yield call(
      Services.auth.fetchCurrentUser,
      appState.loginType || AUTH_TYPE.BASIC_LOGIN
    );
    const token = auth.user?.token || '';
    const clientId = Services.auth.getAuthClientId(
      appState.loginType || AUTH_TYPE.BASIC_LOGIN
    );
    const channel: EventChannel<any> = yield call(websocketForEnquiryChannel, {
      entityId,
      token,
      clientId,
      isNewEnquiry
    });

    while (true) {
      const action: { type: string; payload: any } = yield take(channel);
      yield put(action);
    }
  } catch (error) {
    console.error(error);
  }
}

export function* doDownloadEnquiryCombinedFile(request: any) {
  try {
    const response: {
      File: {
        id: number;
      };
      Job: {
        id: number;
      };
      error?: any;
    } = yield call(
      Services.enquiry.startEnquiryCombinedFileJob,
      request.payload.enquiryId,
      request.payload.fileType,
      request.payload.shareId,
      request.payload.includeLookUpAndLive,
      request.payload.lualMapZoomLevel,
      request.payload.advancedOptions
    );

    if (!response.error) {
      const fileData = {
        jobId: response.Job.id,
        fileId: response.File.id,
        fileType: request.payload.fileType,
        enquiryId: request.payload.enquiryId,
        isFromSwxLocate: request.payload.isFromSwxLocate
      };

      // Directly call the download function without websocket
      try {
        const result = yield call(downloadFileWithPolling, fileData);
        yield put(result);
      } catch (error) {
        yield put(
          downloadEnquiryCombinedFileFailure({
            fileType: request.payload.fileType,
            error: error.message || 'Unknown error occurred'
          })
        );
      }
    } else {
      const errMsg = response.error.message;
      yield put(
        downloadEnquiryCombinedFileFailure({
          fileType: request.payload.fileType,
          error: errMsg
        })
      );
    }
  } catch (error) {
    yield put(
      downloadEnquiryCombinedFileFailure({
        fileType: request.payload.fileType,
        error: `unable to download combined ${request.payload.fileType}`
      })
    );
  }
}

export function* doGetAttachmentPreviewUrl(request: any) {
  try {
    const { fileId, shareId = '', enquiryId = '' } = request.payload;
    const resopnse = yield call(
      Services.enquiry.getAttachmentPreviewUrl,
      fileId,
      shareId,
      enquiryId
    );
    if (!resopnse.error) {
      yield put(getAttachmentPreviewUrlSuccess(resopnse));
    } else {
      const errMsg = resopnse.error?.message;
      yield put(getAttachmentPreviewUrlFailure(errMsg));
    }
  } catch (error) {
    const errorMsg = error?.message ? error.message : error;
    yield put(getAttachmentPreviewUrlFailure(errorMsg));
  }
}

export function* doGetAttachmentDownloadUrl(request: any) {
  try {
    const { fileId, shareId = '', enquiryId = '' } = request.payload;
    const resopnse = yield call(
      Services.enquiry.getAttachmentPreviewUrl,
      fileId,
      shareId,
      enquiryId
    );
    if (!resopnse.error) {
      yield put(getAttachmentDownloadUrlSuccess(resopnse));
    } else {
      const errMsg = resopnse.error?.message;
      yield put(getAttachmentDownloadUrlFailure(errMsg));
    }
  } catch (error) {
    const errorMsg = error?.message ? error.message : error;
    yield put(getAttachmentDownloadUrlFailure(errorMsg));
  }
}

export function* doGetEnquiryConfirmationPDFDownloadUrl(request: any) {
  try {
    const { fileId, enquiryId = '' } = request.payload;
    const resopnse = yield call(
      Services.enquiry.getEnquiryConfirmationPDFDownloadUrl,
      fileId,
      enquiryId
    );
    if (!resopnse.error) {
      yield put(getEnquiryConfirmationPDFDownloadUrlSuccess(resopnse));
    } else {
      const errMsg = resopnse.error?.message;
      yield put(getEnquiryConfirmationPDFDownloadUrlFailure(errMsg));
    }
  } catch (error) {
    const errorMsg = error?.message ? error.message : error;
    yield put(getEnquiryConfirmationPDFDownloadUrlFailure(errorMsg));
  }
}

export function* doGetDownloadAdvancedOptions(request: any) {
  try {
    const { enquiryId = '', shareId = '' } = request.payload;
    const result = yield call(
      Services.enquiry.getAdvancedOptions,
      enquiryId,
      shareId
    );

    if (!result?.error) {
      const organisations = result.Referrals.reduce(
        (acc: { [key: string]: number }, referral: any) => {
          const orgName = referral.Organisation.name;
          acc[orgName] = (acc[orgName] || 0) + 1;
          acc[`${orgName}_index`] = 1;
          return acc;
        },
        {}
      );

      const data: DownloadAdvancedOptions[] = sortBy(
        result.Referrals,
        (item) => item.externalId
      ).map((item: any) => {
        const fixData = {
          externalId: item.externalId,
          isCompleted: item.status === 'COMPLETED',
          organisationName: item.Organisation.name,
          attachments: []
        };

        if (organisations[item.Organisation.name] > 1) {
          const index = organisations[`${item.Organisation.name}_index`];
          fixData.organisationName = `${item.Organisation.name} (${index})`;
          organisations[`${item.Organisation.name}_index`] = index + 1;
        }

        if (item.Responses.length === 0) {
          return fixData;
        }

        const sortResponses = sortBy(item.Responses, (item) => item.id);
        const items: DownloadAdvancedOptionsFile[] = [];

        sortResponses.forEach((response: any, index: number) => {
          const { id: responseId, InboundEmail, Files } = response;

          if (item.Responses.length > 1) {
            items.push({
              type: 'divider',
              id: `${responseId}_divider`,
              name: `Response ${index + 1}`,
              responseId
            });
          }

          items.push({
            type: InboundEmail ? 'mail' : 'response',
            id: responseId,
            responseId,
            name: InboundEmail ? 'Mail Body' : 'Response Body'
          });

          items.push(
            ...sortBy(
              Files?.map(({ id, name }: any) => {
                const extension = name.split('.').pop()?.toLowerCase();

                return { id, name, extension, type: 'attachment', responseId };
              }) || [],
              (item) => item.name.toLowerCase()
            )
          );
        });

        return {
          ...fixData,
          attachments: items
        };
      });

      yield put(
        getDownloadAdvancedOptionsSuccess(
          sortBy(data, (item) => item.organisationName.toLowerCase())
        )
      );
    } else {
      yield put(getDownloadAdvancedOptionsFailure());
    }
  } catch (error) {
    yield put(getDownloadAdvancedOptionsFailure());
  }
}

export function* onFetchEnquiryById() {
  yield takeLatest(EnquiresActionTypes.FETCH_ENQUIRY_BYID, doFetchEnquiryById);
}

export function* onUpdateEnquiryById() {
  yield takeLatest(
    EnquiresActionTypes.UPDATE_ENQUIRY_BYID,
    doUpdateEnquiryById
  );
}

export function* onFetchEnquiryHistory() {
  yield takeLatest(
    EnquiresActionTypes.FETCH_ENQUIRY_HISTORY,
    doFetchEnquiryHistory
  );
}

export function* onConnectWebsocketForEnquiry() {
  yield takeLatest(
    EnquiresActionTypes.CONNECT_WEBSOCKET_FOR_ENQUIRY,
    doConnectWebsocketForEnquiry
  );
}

export function* onDownloadEnquiryCombinedFile() {
  yield takeLatest(
    EnquiresActionTypes.DOWNLOAD_ENQUIRY_COMBINED_FILE_START,
    doDownloadEnquiryCombinedFile
  );
}

export function* onGetAttachmentPreviewUrl() {
  yield takeLatest(
    EnquiresActionTypes.GET_ATTACHMENT_PREVIEW_URL_START,
    doGetAttachmentPreviewUrl
  );
}

export function* onGetAttachmentDownloadUrl() {
  yield takeLatest(
    EnquiresActionTypes.GET_ATTACHMENT_DOWNLOAD_URL_START,
    doGetAttachmentDownloadUrl
  );
}

export function* onGetEnquiryConfirmationPDFDownloadUrl() {
  yield takeLatest(
    EnquiresActionTypes.GET_ENQUIRY_CONFIRMATION_PDF_DOWNLOAD_URL_START,
    doGetEnquiryConfirmationPDFDownloadUrl
  );
}

export function* onGetDownloadAdvancedOptions() {
  yield takeLatest(
    EnquiresActionTypes.GET_DOWNLOAD_ADVANCED_OPTIONS_START,
    doGetDownloadAdvancedOptions
  );
}

export function* enquiriesSagas() {
  yield all([
    call(onFetchEnquiryHistory),
    call(onFetchEnquiryById),
    call(onUpdateEnquiryById),
    call(onConnectWebsocketForEnquiry),
    call(onDownloadEnquiryCombinedFile),
    call(onGetAttachmentPreviewUrl),
    call(onGetAttachmentDownloadUrl),
    call(onGetEnquiryConfirmationPDFDownloadUrl),
    call(onGetDownloadAdvancedOptions)
  ]);
}

function websocketForEnquiryChannel({
  entityId,
  token,
  clientId,
  isNewEnquiry
}: {
  entityId: number;
  token: string;
  clientId: string;
  isNewEnquiry: boolean;
}) {
  return eventChannel((emitter) => {
    const onClose = () => {
      return emitter(connectWebsocketForEnquiry(entityId));
    };

    const onMessage = async (event: MessageEvent) => {
      try {
        const body = JSON.parse(event.data);
        const { referralId, status } = body;
        if (status === 'COMPLETED') {
          const referral = await Services.referral.fetchReferralByIdService(
            entityId,
            referralId,
            true,
            false,
            true
          );
          if (referral) {
            referral.isNewResponses = true;
          }
          if (isNewEnquiry) {
            return emitter(updateLodgeEnquiryReferral(referral));
          } else {
            return emitter(updateEnquiryReferral(referral));
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    const option: WebSocketConnectionOption = {
      entity: 'ENQUIRY',
      entityId,
      token,
      clientId,
      onMessage,
      onClose
    };

    Services.websocket.createWebSocketConnection(option);
    // unsubscribe function
    return () => {
      // have to put something for es-lint
    };
  });
}

function* downloadFileWithPolling(fileData: any) {
  try {
    const { jobId, fileId, fileType, enquiryId, isFromSwxLocate } = fileData;

    // Poll for job completion
    let jobStatus = 'PROCESSING';
    let attempts = 0;
    const maxAttempts = 180; // Will fail after 3 minutes
    const successStatuses = ['COMPLETED', 'COMPLETED_WITH_ERROR'];

    while (jobStatus !== 'COMPLETED' && attempts < maxAttempts) {
      const jobDetails = yield call(
        Services.enquiry.fetchJobDetailsById,
        jobId,
        isFromSwxLocate ? enquiryId : undefined
      );

      jobStatus = jobDetails.status;

      if (successStatuses.includes(jobStatus)) {
        break;
      } else if (jobStatus === 'FAILED' || jobStatus === 'ERROR') {
        throw new Error(`Job failed with status: ${jobStatus}`);
      }

      // Wait 1 second before next check
      yield delay(1000);
      attempts++;
    }

    if (!successStatuses.includes(jobStatus)) {
      throw new Error(
        `Job did not complete within expected time. Status: ${jobStatus}`
      );
    }

    // Download the file
    const file = yield call(
      Services.enquiry.downloadEnquiryCombinedFile,
      fileId,
      enquiryId
    );

    yield call(Utils.getEmailAttachment, {
      url: file.url,
      attachmentName: file.name
    });

    return downloadEnquiryCombinedFileSuccess({ fileType });
  } catch (error) {
    console.error('Error in downloadFileWithPolling:', error);
    throw downloadEnquiryCombinedFileFailure({
      fileType: fileData.fileType,
      error: error.message || 'Unknown error occurred'
    });
  }
}

export default enquiriesSagas;
